import { useCallback, useEffect, useState } from 'react';
import { db } from '../lib/supabase';

/**
 * Generic admin CRUD table — guarantees add/edit/delete for every module.
 *
 * columns: [{
 *   key, label,
 *   type: 'text'|'number'|'textarea'|'checkbox'|'date'|'datetime'|'select'|'json',
 *   options: [{value,label}]      (for select)
 *   inTable: true, inForm: true,  (visibility)
 *   render: (row) => node,        (custom cell)
 *   required: bool
 * }]
 */
export default function CrudTable({
  title,
  table,
  idCol = 'id',
  columns,
  defaults = {},
  orderBy = 'created_at',
  ascending = false,
  match,
  fetchRows,           // optional override: async () => rows
  beforeSave,          // optional: (values, isNew) => values | throws
  afterChange,         // optional: () => void
  canAdd = true,
  canEdit = true,
  canDelete = true,
  rowActions,          // optional: (row, reload) => node
  addLabel = '+ Thêm mới',
  refreshKey = 0,
  emptyText = 'Chưa có dữ liệu. Bấm "Thêm mới" để tạo item đầu tiên.',
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // null | {} (new) | row
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (fetchRows) {
        setRows((await fetchRows()) || []);
      } else {
        const { data, error: e } = await db.list(table, { orderBy, ascending, match });
        if (e) throw e;
        setRows(data || []);
      }
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [table, orderBy, ascending, JSON.stringify(match), fetchRows]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load, refreshKey]);

  const openNew = () => { setValues({ ...defaults }); setEditing({}); };
  const openEdit = (row) => { setValues({ ...row }); setEditing(row); };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const isNew = !editing?.[idCol];
      let v = { ...values };
      columns.forEach((c) => {
        if (c.type === 'number' && v[c.key] === '') v[c.key] = null;
        if ((c.type === 'date' || c.type === 'datetime') && v[c.key] === '') v[c.key] = null;
      });
      if (beforeSave) v = await beforeSave(v, isNew);
      if (isNew) {
        const { error: e } = await db.insert(table, v);
        if (e) throw e;
      } else {
        const id = editing[idCol];
        const patch = { ...v };
        delete patch[idCol];
        // strip nested/join fields not belonging to the table
        Object.keys(patch).forEach((k) => { if (typeof patch[k] === 'object' && patch[k] !== null && !Array.isArray(patch[k]) && !(columns.find(c=>c.key===k)?.type === 'json')) delete patch[k]; });
        const { error: e } = await db.update(table, id, patch, idCol);
        if (e) throw e;
      }
      setEditing(null);
      await load();
      afterChange?.();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  const del = async (row) => {
    if (!window.confirm('Xoá item này? Hành động không thể hoàn tác.')) return;
    const { error: e } = await db.remove(table, row[idCol], idCol);
    if (e) { setError(e.message); return; }
    await load();
    afterChange?.();
  };

  const tableCols = columns.filter((c) => c.inTable !== false);
  const formCols = columns.filter((c) => c.inForm !== false);

  const cell = (row, c) => {
    if (c.render) return c.render(row);
    const v = row[c.key];
    if (c.type === 'checkbox') return v ? '✅' : '—';
    if (c.type === 'select') return c.options?.find((o) => String(o.value) === String(v))?.label ?? (v ?? '—');
    if (c.type === 'json') return v ? <code style={{ fontSize: 11 }}>{JSON.stringify(v).slice(0, 60)}…</code> : '—';
    if (c.type === 'datetime' && v) return new Date(v).toLocaleString('vi-VN');
    if (v === null || v === undefined || v === '') return '—';
    const s = String(v);
    return s.length > 80 ? s.slice(0, 80) + '…' : s;
  };

  const field = (c) => {
    const v = values[c.key] ?? '';
    const set = (val) => setValues((p) => ({ ...p, [c.key]: val }));
    if (c.type === 'checkbox')
      return (
        <label key={c.key} className="checkbox-row">
          <input type="checkbox" checked={!!values[c.key]} onChange={(e) => set(e.target.checked)} /> {c.label}
        </label>
      );
    const cls = c.type === 'textarea' || c.type === 'json' ? 'full' : '';
    return (
      <label key={c.key} className={`field ${cls}`}>
        <span>{c.label}{c.required ? ' *' : ''}</span>
        {c.type === 'textarea' ? (
          <textarea value={v} onChange={(e) => set(e.target.value)} />
        ) : c.type === 'json' ? (
          <textarea
            value={typeof v === 'object' && v !== null ? JSON.stringify(v, null, 2) : v}
            onChange={(e) => set(e.target.value)}
            style={{ fontFamily: 'monospace', fontSize: 13 }}
          />
        ) : c.type === 'select' ? (
          <select value={v ?? ''} onChange={(e) => set(e.target.value)}>
            <option value="">—</option>
            {c.options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ) : (
          <input
            type={c.type === 'number' ? 'number' : c.type === 'date' ? 'date' : c.type === 'datetime' ? 'datetime-local' : 'text'}
            value={v ?? ''}
            onChange={(e) => set(e.target.value)}
          />
        )}
      </label>
    );
  };

  return (
    <div>
      <div className="admin-head">
        <h3 style={{ margin: 0 }}>{title}</h3>
        {canAdd && <button className="btn btn-primary btn-sm" onClick={openNew}>{addLabel}</button>}
      </div>
      {error && <div className="banner" style={{ background: 'var(--red-soft)', color: 'var(--red)' }}>{error}</div>}
      {loading ? (
        <div className="loading">Đang tải...</div>
      ) : rows.length === 0 ? (
        <div className="empty card">{emptyText}</div>
      ) : (
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                {tableCols.map((c) => <th key={c.key}>{c.label}</th>)}
                <th style={{ width: 130 }}>Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row[idCol]}>
                  {tableCols.map((c) => <td key={c.key}>{cell(row, c)}</td>)}
                  <td>
                    <div className="row" style={{ gap: 4, flexWrap: 'nowrap' }}>
                      {rowActions?.(row, load)}
                      {canEdit && <button className="btn btn-secondary btn-sm" onClick={() => openEdit(row)}>Sửa</button>}
                      {canDelete && <button className="btn btn-danger btn-sm" onClick={() => del(row)}>Xoá</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing !== null && (
        <div className="modal-back" onClick={(e) => e.target === e.currentTarget && setEditing(null)}>
          <div className="modal">
            <h3>{editing[idCol] ? 'Sửa item' : 'Thêm mới'} — {title}</h3>
            <div className="form-grid">{formCols.map(field)}</div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setEditing(null)}>Huỷ</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Đang lưu...' : 'Lưu'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
